if (!customElements.get('orsay-stories')) {
	class OrsayStories extends HTMLElement {
		static get CONSTANTS() {
			return {
				TRANSITION_DURATION: 300,
				VERTICAL_THRESHOLD_RATIO: 0.15,
				HORIZONTAL_THRESHOLD: 50,
				MAX_ROTATION: 53,
				ELASTIC_ROTATION: 4,
				CLICK_THRESHOLD_PERCENTAGE: 20
			}
		}

		constructor() {
			super()
			this.state = {
				currentStoryIndex: 0,
				transitionDuration: this.constructor.CONSTANTS.TRANSITION_DURATION,
				isDragging: false,
				touchStart: { x: 0, y: 0 },
				touchEnd: { x: 0, y: 0 }
			}

			this.wasMuted = true

			this.elements = {
				feedContainer: this.querySelector('.orsay-stories__feed-container'),
				feedBackgroundOverlay: this.querySelector('.orsay-stories__feed-background-overlay'),
				slides: this.querySelectorAll('.orsay-video-content-container')
			}

			this.handleVisibilityChange = this.#handleVisibilityChange.bind(this)
		}

		connectedCallback() {
			this.#initializeEventListeners()
			document.addEventListener('visibilitychange', this.handleVisibilityChange)
		}

		disconnectedCallback() {
			document.removeEventListener('visibilitychange', this.handleVisibilityChange)
		}

		#handleVisibilityChange() {
			if (document.hidden) {
				this.wasMuted = this.#isCurrentStoryMuted()
				this.muteCurrentStory()
				this.pauseCurrentStory()
			} else {
				if (this.wasMuted) {
					this.unmuteCurrentStory()
				}
				this.playCurrentStory()
			}
		}

		#initializeEventListeners() {
			this.#handleCloseEvents()
			this.#handleKeyboardEvents()
			this.#handleTouchEvents()
			this.#initializeSlideControls()
		}

		#handleCloseEvents() {
			this.addEventListener('click', event => {
				if (!event.target.closest('.orsay-story') && !event.target.closest('.orsay-stories__arrow')) {
					this.hide()
				}
			})
		}

		#handleKeyboardEvents() {
			document.addEventListener('keydown', event => {
				switch (event.key) {
					case 'Escape':
						this.hide()
						break
					case 'ArrowLeft':
						this.goToStory(this.state.currentStoryIndex - 1)
						break
					case 'ArrowRight':
						this.goToStory(this.state.currentStoryIndex + 1)
						break
				}
			})
		}

		#handleTouchEvents() {
			this.elements.feedContainer.addEventListener('touchstart', e => {
				if (!e.target.closest('.orsay-video-content-container')) return
				this.#handleTouchStart(e)
			})

			this.elements.feedContainer.addEventListener('touchmove', e => {
				if (!e.target.closest('.orsay-video-content-container')) return
				this.#handleTouchMove(e)
			})

			this.elements.feedContainer.addEventListener('touchend', e => {
				if (!e.target.closest('.orsay-video-content-container')) return
				this.#handleTouchEnd(e)
			})

			this.elements.feedContainer.addEventListener('touchcancel', e => {
				if (!e.target.closest('.orsay-video-content-container')) return
				this.#handleTouchCancel(e)
			})
		}

		#initializeSlideControls() {
			this.elements.slides.forEach(slide => {
				this.#initializeSingleSlideControls(slide)
			})
		}

		#initializeSingleSlideControls(slide) {
			const controls = {
				videoContent: slide.querySelector('.orsay-story'),
				closeButton: slide.querySelector('.orsay-story__close-button'),
				leftArrow: slide.querySelector('.orsay-stories__arrow--left'),
				rightArrow: slide.querySelector('.orsay-stories__arrow--right')
			}

			const storyElement = slide.querySelector('orsay-story')
			if (storyElement) {
				storyElement.addEventListener('story-ended', () => {
					this.goToStory(this.state.currentStoryIndex + 1)
				})
			}

			this.#setupVideoContentControls(controls)
			this.#setupNavigationControls(controls)

			if (controls.closeButton) {
				controls.closeButton.addEventListener('click', event => {
					event.stopPropagation()

					this.elements.slides.forEach(slide => {
						const storyElement = slide.querySelector('orsay-story')
						if (storyElement) {
							storyElement.mute()

							const video = storyElement.querySelector('video')
							if (video) {
								video.pause()
								video.muted = true
								video.currentTime = 0
							}
						}
					})

					this.hide()
				})
			}
		}

		#handleTouchStart(event) {
			this.state.isDragging = true
			this.state.touchStart = {
				x: event.touches[0].clientX,
				y: event.touches[0].clientY
			}
		}

		#handleTouchMove(event) {
			if (!this.state.isDragging) return

			this.state.touchEnd = {
				x: event.touches[0].clientX,
				y: event.touches[0].clientY
			}

			const dragDistance = {
				x: this.state.touchEnd.x - this.state.touchStart.x,
				y: this.state.touchEnd.y - this.state.touchStart.y
			}

			if (Math.abs(dragDistance.y) > Math.abs(dragDistance.x)) {
				this.#handleVerticalDrag(dragDistance.y)
			} else {
				this.#handleHorizontalDrag(dragDistance.x)
			}
		}

		#handleTouchEnd() {
			if (!this.state.isDragging) return
			this.state.isDragging = false

			const dragDistance = {
				x: this.state.touchEnd.x - this.state.touchStart.x,
				y: this.state.touchEnd.y - this.state.touchStart.y
			}

			if (Math.abs(dragDistance.y) > Math.abs(dragDistance.x)) {
				this.#handleVerticalDragEnd(dragDistance.y)
			} else {
				this.#handleHorizontalDragEnd(dragDistance.x)
			}
		}

		#handleTouchCancel() {
			if (!this.state.isDragging) return
			this.state.isDragging = false
			this.#resetDragState()
		}

		#handleVerticalDrag(dragDistanceY) {
			const screenHeight = window.innerHeight
			const dragPercentageY = (dragDistanceY / screenHeight) * 100
			const opacity = Math.max(0, 1 - Math.abs(dragPercentageY) / 50)

			this.elements.feedContainer.style.transform = `translateY(${dragDistanceY}px)`
			this.elements.feedContainer.style.opacity = opacity.toString()
			this.elements.feedBackgroundOverlay.style.opacity = opacity.toString()
		}

		#handleHorizontalDrag(dragDistanceX) {
			if (this.#isEdgeSlide(dragDistanceX)) return

			const dragPercentageX = (dragDistanceX / window.innerWidth) * 100
			const { MAX_ROTATION, ELASTIC_ROTATION } = this.constructor.CONSTANTS

			let currentRotation
			if (this.#isEdgeSlide(dragDistanceX)) {
				currentRotation = Math.min(Math.abs(dragPercentageX), ELASTIC_ROTATION) * Math.sign(dragDistanceX)
			} else {
				currentRotation = Math.min(Math.abs(dragPercentageX), MAX_ROTATION) * Math.sign(dragPercentageX)
			}

			this.#rotateCurrentSlide(currentRotation)
			this.#rotateAdjacentSlide(dragDistanceX, currentRotation)
		}

		#handleVerticalDragEnd(dragDistanceY) {
			const verticalThreshold = window.innerHeight * this.constructor.CONSTANTS.VERTICAL_THRESHOLD_RATIO
			this.#addTransitionStyles()

			if (Math.abs(dragDistanceY) > verticalThreshold) {
				this.hide()
			} else {
				this.#resetDragState()
			}

			this.#removeTransitionStyles()
		}

		#handleHorizontalDragEnd(dragDistanceX) {
			const horizontalThreshold = 50
			this.#addTransitionStyles()

			if (Math.abs(dragDistanceX) > horizontalThreshold) {
				if (dragDistanceX > 0 && this.state.currentStoryIndex > 0) {
					this.goToStory(this.state.currentStoryIndex - 1)
				} else if (dragDistanceX < 0 && this.state.currentStoryIndex < this.elements.slides.length - 1) {
					this.goToStory(this.state.currentStoryIndex + 1)
				}
			}

			this.#resetSlideRotations()
			this.#removeTransitionStyles()
		}

		#setupVideoContentControls({ videoContent }) {
			if (!videoContent) return

			videoContent.addEventListener('click', event => {
				if (event.target.closest('.orsay-story__controls')) return

				const clickX = event.clientX - videoContent.getBoundingClientRect().left
				const clickPercentage = (clickX / videoContent.offsetWidth) * 100

				if (clickPercentage <= this.constructor.CONSTANTS.CLICK_THRESHOLD_PERCENTAGE) {
					this.goToStory(this.state.currentStoryIndex - 1)
				} else {
					this.goToStory(this.state.currentStoryIndex + 1)
				}
			})
		}

		#setupNavigationControls({ leftArrow, rightArrow }) {
			if (leftArrow) {
				leftArrow.addEventListener('click', () => {
					this.goToStory(this.state.currentStoryIndex - 1)
				})
			}

			if (rightArrow) {
				rightArrow.addEventListener('click', () => {
					this.goToStory(this.state.currentStoryIndex + 1)
				})
			}
		}

		goToStory(index) {
			if (index < 0 || index >= this.elements.slides.length) {
				this.hide()
				return
			}

			const isMuted = this.#isCurrentStoryMuted()

			this.currentStoryElement = this.elements.slides[index]
			this.state.currentStoryIndex = index
			this.#updateStoriesVisibility()
			this.stopAllStories()
			this.playCurrentStory()

			if (!isMuted) {
				this.unmuteCurrentStory()
			} else {
				this.muteCurrentStory()
			}
		}

		#isCurrentStoryMuted() {
			const storyElement = this.currentStoryElement?.querySelector('orsay-story')
			if (!storyElement || !storyElement.video) return true
			return storyElement.video.muted
		}

		show() {
			const orsayStickyStories = document.querySelector('orsay-sticky-stories')
			if (orsayStickyStories) {
				const storiesContainer = orsayStickyStories.querySelector('.orsay-sticky-stories__stories-container')
				if (storiesContainer.hasAttribute('open')) {
					orsayStickyStories.querySelector('.orsay-sticky-stories__sticky-story-close-button')?.click()
				}
			}

			this.elements.feedContainer.classList.remove('hidden')
			this.elements.feedBackgroundOverlay.classList.remove('hidden')

			setTimeout(() => {
				this.elements.feedContainer.classList.add('orsay-stories__feed-container--active')
				this.elements.feedBackgroundOverlay.classList.add('orsay-stories__feed-background-overlay--active')
			}, 10)

			document.body.style.overflow = 'hidden'

			const currentSlide = this.elements.slides[this.state.currentStoryIndex]
			currentSlide.setAttribute('aria-modal', 'true')

			this.unmuteCurrentStory()
			this.playCurrentStory()
		}

		hide() {
			this.#resetDragState()

			this.elements.slides.forEach(slide => {
				slide.setAttribute('aria-modal', 'false')

				const storyElement = slide.querySelector('orsay-story')
				if (storyElement) {
					storyElement.mute()
					storyElement.reset()

					const video = storyElement.querySelector('video')
					if (video) {
						video.pause()
						video.muted = true
						video.currentTime = 0
					}
				}
			})

			this.elements.feedContainer.classList.remove('orsay-stories__feed-container--active')
			this.elements.feedBackgroundOverlay.classList.remove('orsay-stories__feed-background-overlay--active')

			setTimeout(() => {
				this.elements.feedContainer.classList.add('hidden')
				this.elements.feedBackgroundOverlay.classList.add('hidden')
				this.elements.feedContainer.style.transformOrigin = ''
			}, this.state.transitionDuration)

			this.stopAllStories()
			this.resetAllSlides()
			this.dataset.open = false
			document.body.style.overflow = 'auto'
		}

		stopAllStories() {
			this.elements.slides.forEach(slide => {
				const storyElement = slide.querySelector('orsay-story')
				if (storyElement) {
					storyElement.reset()
				}
			})
		}

		playCurrentStory() {
			const storyElement = this.currentStoryElement?.querySelector('orsay-story')
			if (!storyElement) return
			storyElement.play()
		}

		pauseCurrentStory() {
			const storyElement = this.currentStoryElement?.querySelector('orsay-story')
			if (!storyElement) return
			storyElement.pause()
		}

		muteCurrentStory() {
			const storyElement = this.currentStoryElement?.querySelector('orsay-story')
			if (!storyElement) return
			storyElement.mute()
		}

		unmuteCurrentStory() {
			const storyElement = this.currentStoryElement?.querySelector('orsay-story')
			if (!storyElement) return
			storyElement.unmute()
		}

		#updateStoriesVisibility() {
			this.elements.slides.forEach((slide, index) => {
				const position = this.#getSlidePosition(index)
				slide.dataset.state = position
			})
		}

		#getSlidePosition(index) {
			if (index === this.state.currentStoryIndex) return 'current'
			if (index === this.state.currentStoryIndex + 1) return 'next'
			if (index === this.state.currentStoryIndex - 1) return 'prev'
			if (index > this.state.currentStoryIndex + 1) return 'hidden-next'
			return 'hidden-prev'
		}

		toggleControlButtons(inactiveButton, activeButton) {
			if (!inactiveButton || !activeButton) return

			inactiveButton.classList.remove('orsay-story__controls-button--active')
			activeButton.classList.add('orsay-story__controls-button--active')
		}

		#isEdgeSlide(dragDistanceX) {
			return (
				(this.state.currentStoryIndex === 0 && dragDistanceX > 0) ||
				(this.state.currentStoryIndex === this.elements.slides.length - 1 && dragDistanceX < 0)
			)
		}

		#resetDragState() {
			this.elements.feedContainer.style.transform = ''
			this.elements.feedContainer.style.opacity = ''
			this.elements.feedBackgroundOverlay.style.opacity = ''
		}

		resetAllSlides() {
			this.elements.slides.forEach(slide => {
				slide.dataset.state = 'hidden-next'
			})
		}

		#addTransitionStyles() {
			this.elements.feedContainer.style.transition = `transform ${this.state.transitionDuration}ms ease-out, opacity ${this.state.transitionDuration}ms ease-out`
			this.elements.feedBackgroundOverlay.style.transition = `opacity ${this.state.transitionDuration}ms ease-out`
			this.elements.slides.forEach(slide => {
				slide.style.transition = `transform ${this.state.transitionDuration}ms ease-out`
			})
		}

		#removeTransitionStyles() {
			setTimeout(() => {
				this.elements.feedContainer.style.transition = ''
				this.elements.feedBackgroundOverlay.style.transition = ''
				this.elements.slides.forEach(slide => {
					slide.style.transition = ''
				})
			}, this.state.transitionDuration)
		}

		#resetSlideRotations() {
			this.elements.slides.forEach(slide => {
				slide.style.transform = ''
			})
		}

		#rotateCurrentSlide(rotation) {
			const currentSlide = this.elements.slides[this.state.currentStoryIndex]
			currentSlide.style.transform = `translateZ(calc(-1 * var(--screen-width))) rotateY(${rotation}deg)`
		}

		#rotateAdjacentSlide(dragDistanceX, currentRotation) {
			if (dragDistanceX > 0 && this.state.currentStoryIndex > 0) {
				const prevSlide = this.elements.slides[this.state.currentStoryIndex - 1]
				prevSlide.style.transform = `translateZ(calc(-1 * var(--screen-width))) rotateY(${-53 + Math.abs(currentRotation)}deg)`
			} else if (dragDistanceX < 0 && this.state.currentStoryIndex < this.elements.slides.length - 1) {
				const nextSlide = this.elements.slides[this.state.currentStoryIndex + 1]
				nextSlide.style.transform = `translateZ(calc(-1 * var(--screen-width))) rotateY(${53 - Math.abs(currentRotation)}deg)`
			}
		}
	}

	customElements.define('orsay-stories', OrsayStories)
}

if (!customElements.get('orsay-story')) {
	class OrsayStory extends HTMLElement {
		constructor() {
			super()
			this.video = this.querySelector('video')
			this.progressBar = this.querySelector('.orsay-story__progress-bar-line')
			this.controls = {
				closeButton: this.querySelector('.orsay-story__close-button'),
				pauseButton: this.querySelector('.orsay-story__controls-button--pause'),
				playButton: this.querySelector('.orsay-story__controls-button--play'),
				muteButton: this.querySelector('.orsay-story__controls-button--mute'),
				unmuteButton: this.querySelector('.orsay-story__controls-button--unmute')
			}

			this.animationFrameId = null
			this.isPlaying = false
		}

		connectedCallback() {
			this.#initializeControls()
			this.#setupVideoEvents()
		}

		disconnectedCallback() {
			this.#cleanup()
		}

		#initializeControls() {
			if (this.controls.pauseButton && this.controls.playButton) {
				this.controls.pauseButton.addEventListener('click', () => this.pause())
				this.controls.playButton.addEventListener('click', () => this.play())
			}

			if (this.controls.muteButton && this.controls.unmuteButton) {
				this.controls.muteButton.addEventListener('click', () => this.mute())
				this.controls.unmuteButton.addEventListener('click', () => this.unmute())
			}
		}

		#setupVideoEvents() {
			if (!this.video) return

			this.video.addEventListener('play', () => {
				this.isPlaying = true
				this.#updateProgress()
				this.showControl(this.controls.pauseButton, this.controls.playButton)
			})

			this.video.addEventListener('pause', () => {
				this.isPlaying = false
				this.#cancelProgressUpdate()
				this.showControl(this.controls.playButton, this.controls.pauseButton)
			})

			this.video.addEventListener('ended', () => {
				this.isPlaying = false
				this.#cancelProgressUpdate()
				this.dispatchEvent(new CustomEvent('story-ended'))
			})
		}

		#updateProgress() {
			if (!this.video || !this.progressBar) return

			const updateFrame = () => {
				if (!this.isPlaying) return

				const progress = this.video.currentTime / this.video.duration
				this.progressBar.style.transform = `scaleX(${progress})`
				this.animationFrameId = requestAnimationFrame(updateFrame)
			}

			this.animationFrameId = requestAnimationFrame(updateFrame)
		}

		#cancelProgressUpdate() {
			if (this.animationFrameId) {
				cancelAnimationFrame(this.animationFrameId)
				this.animationFrameId = null
			}
		}

		play() {
			if (!this.video) return
			this.video.play()
			this.showControl(this.controls.pauseButton, this.controls.playButton)
		}

		pause() {
			if (!this.video) return
			this.video.pause()
			this.showControl(this.controls.playButton, this.controls.pauseButton)
		}

		mute() {
			if (!this.video) return
			this.video.muted = true
			this.showControl(this.controls.unmuteButton, this.controls.muteButton)
		}

		unmute() {
			if (!this.video) return
			this.video.muted = false
			this.showControl(this.controls.muteButton, this.controls.unmuteButton)
		}

		showControl(activeButton, inactiveButton) {
			if (!activeButton || !inactiveButton) return

			inactiveButton.classList.remove('orsay-story__controls-button--active')
			activeButton.classList.add('orsay-story__controls-button--active')
		}

		reset() {
			if (!this.video) return
			this.video.pause()
			this.video.currentTime = 0
			if (this.progressBar) {
				this.progressBar.style.transform = 'scaleX(0)'
			}
		}

		#cleanup() {
			this.#cancelProgressUpdate()
		}
	}

	customElements.define('orsay-story', OrsayStory)
}

if (!customElements.get('orsay-sticky-stories')) {
	class OrsayStickyStories extends HTMLElement {
		constructor() {
			super()
			this.previewStory = this.querySelector('.orsay-sticky-stories__sticky-story')
			this.previewStoryVideoContainer = this.querySelector('.orsay-sticky-stories__sticky-story-video-container')
			this.previewStoryCloseButton = this.querySelector('.orsay-sticky-stories__sticky-story-close-button')
			this.storiesContainer = this.querySelector('.orsay-sticky-stories__stories-container')
			this.stories = this.querySelectorAll('.orsay-sticky-stories__story-container')
			this.storyElements = this.querySelectorAll('orsay-story')
			this.currentIndex = 0
			this.hidden = false
			this.CLICK_THRESHOLD_PERCENTAGE = 20
		}

		connectedCallback() {
			this.#setEventListeners()
		}

		#setEventListeners() {
			this.previewStoryVideoContainer.addEventListener('click', event => {
				this.#openPlayer()
				this.#goToStory(0)
			})

			this.previewStoryCloseButton.addEventListener('click', event => {
				event.preventDefault()
				this.#muteAllStories()
				this.#closePlayer()
				this.#hideStickyStory()
				this.hidden = true
			})

			this.storiesContainer.addEventListener('click', event => {
				event.stopPropagation()
			})

			this.stories.forEach((story, index) => {
				const storyElement = story.querySelector('orsay-story')

				storyElement.addEventListener('story-ended', () => {
					this.#goToStory(index + 1)
				})

				story.querySelector('.orsay-story__close-button').addEventListener('click', event => {
					event.stopPropagation()
					this.#muteAllStories()
					this.#closePlayer()
					storyElement.reset()
				})

				this.#setupVideoContentControls(story, index)
			})

			document.addEventListener('click', event => {
				if (!this.storiesContainer.contains(event.target) && !this.previewStory.contains(event.target) && !this.hidden) {
					this.#muteAllStories()
					this.#closePlayer()
				}
			})
		}

		#setupVideoContentControls(story) {
			const videoContent = story.querySelector('.orsay-story')
			if (!videoContent) return

			videoContent.addEventListener('click', event => {
				if (event.target.closest('.orsay-story__controls')) return

				const clickX = event.clientX - videoContent.getBoundingClientRect().left
				const clickPercentage = (clickX / videoContent.offsetWidth) * 100

				if (clickPercentage <= this.CLICK_THRESHOLD_PERCENTAGE) {
					this.#goToStory(this.currentIndex - 1)
				} else if (clickPercentage >= 100 - this.CLICK_THRESHOLD_PERCENTAGE) {
					this.#goToStory(this.currentIndex + 1)
				}
			})
		}

		#goToStory(index) {
			if (index < 0 || index >= this.stories.length) {
				this.#closePlayer()
				return
			}

			if (this.storyElements[this.currentIndex]) {
				this.storyElements[this.currentIndex].reset()
			}

			this.currentIndex = index

			this.stories.forEach((story, i) => {
				if (i === index) {
					story.dataset.state = 'current'
				} else if (i < index) {
					story.dataset.state = 'prev'
				} else {
					story.dataset.state = 'next'
				}
			})

			if (this.storyElements[index]) {
				this.storyElements[index].play()
				this.#unmuteCurrentStory()
			}
		}

		#hideStickyStory() {
			this.previewStory.removeAttribute('visible')
		}

		#openPlayer() {
			const orsayStories = document.querySelector('.orsay-stories')
			if (orsayStories && orsayStories.dataset.open === 'true') {
				orsayStories.hide()
			}

			this.storiesContainer.setAttribute('open', true)
			this.previewStory.removeAttribute('visible')

			if (this.storyElements[this.currentIndex]) {
				this.#unmuteCurrentStory()
			}
		}

		#closePlayer() {
			this.storiesContainer.removeAttribute('open')
			this.previewStory.setAttribute('visible', true)

			// Reset and pause videos
			this.storyElements.forEach(story => {
				story.reset()
				const video = story.querySelector('video')
				if (video) {
					video.pause()
					video.currentTime = 0
				}
			})
		}

		#unmuteCurrentStory() {
			const storyElement = this.storyElements[this.currentIndex]
			if (storyElement) {
				storyElement.unmute()
			}
		}

		#muteAllStories() {
			this.storyElements.forEach(story => {
				story.mute()
				const video = story.querySelector('video')
				if (video) {
					video.muted = true
				}
			})
		}
	}

	customElements.define('orsay-sticky-stories', OrsayStickyStories)
}

document.addEventListener('DOMContentLoaded', () => {
	document.querySelectorAll('.instagram')?.forEach(section => {
		const stories = section.querySelector('.orsay-stories')
		if (!stories) {
			console.error('No story container found')
			return
		} else {
			section.querySelectorAll('.orsay-card-story')?.forEach((cardStory, index) => {
				const video = cardStory.querySelector('video')
				if (!video) return
				cardStory.addEventListener('mouseenter', () => {
					video.play()
				})
				cardStory.addEventListener('mouseleave', () => {
					video.pause()
					video.currentTime = 0
				})
				cardStory.addEventListener('click', event => {
					stories.querySelector('.orsay-stories__feed-container').style.transformOrigin = `${event.clientX}px ${event.clientY}px`
					stories.goToStory(index)
					stories.show()
				})
			})
		}
	})
})
